<!--
  Visual hierarchy (AGENTS.md §Visual hierarchy)
  Question: "Is my export ready, and can I get it?"
  Answer:   the status of the most recent job - a queued/generating row carries a quiet
            spinner (the one indicator this system lets loop, and it stops the moment its
            data lands), while completed and failed are outcomes and therefore always carry
            an icon plus a tinted surface via StatusTag, never colour alone. The download
            control appears only once a job is actually a file - it is never rendered
            disabled while waiting, because a disabled control the user cannot resolve is
            worse than one that is not there yet.
  Context:  when a job was requested, its date range, and who requested it - regular
            weight, muted, matching the transactions table's own column treatment.
  Controls: the date-range form is the one primary action this screen has, so its submit
            button is the only --color-action control above the fold; "Try again" in the
            error state mirrors the transactions screen for the same reason.
-->
<script setup lang="ts">
import { PageHeader, PageShell } from '@taler/ui'
import { plusDays, systemClock, todayUtc } from '@taler/domain'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  CreateExportForm,
  type CreateExportInput,
  ExportList,
  type ExportJob,
  canViewExports,
  useExports,
} from '@/features/exports'
import { useSession } from '@/app/session'

const { t } = useI18n()
const session = useSession()

const canView = computed(() => canViewExports(session.can))

const exports = useExports({
  ledgerId: () => session.activeLedger.value?.id,
  enabled: () => canView.value,
  format: 'CSV',
})

const today = todayUtc(systemClock)
const defaultFrom = plusDays(today, -29)

function onSubmit(input: CreateExportInput): void {
  exports.create(input)
}

function onDownload(job: ExportJob): void {
  exports.download(job)
}
</script>

<template>
  <PageShell class="gap-6">
    <PageHeader :title="t('exports.title')" />

    <p v-if="session.isLoading.value" role="status">{{ t('app.loading') }}</p>

    <p v-else-if="session.activeLedger.value === undefined" class="text-[color:var(--color-text-muted)]">
      {{ t('exports.noLedger') }}
    </p>

    <p v-else-if="!canView" class="text-[color:var(--color-text-muted)]">
      {{ t('exports.noAccess') }}
    </p>

    <template v-else>
      <CreateExportForm
        format="CSV"
        :default-from="defaultFrom"
        :default-until="today"
        :is-creating="exports.isCreating.value"
        :error="exports.createError.value"
        @submit="onSubmit"
      />

      <ExportList
        :jobs="exports.jobs.value"
        :is-loading="exports.isLoading.value"
        :error="exports.error.value"
        :retrying="exports.retrying.value"
        :downloading-id="exports.downloadingId.value"
        @retry="exports.retry()"
        @download="onDownload"
      />
    </template>
  </PageShell>
</template>
